import { ReactElement, useEffect, useCallback, useMemo, useState } from "react";
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  useSortable,
  rectSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import {
  Avatar,
  Box,
  Button,
  Collapse,
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerHeader,
  DrawerOverlay,
  Flex,
  FormControl,
  FormLabel,
  HStack,
  IconButton,
  Link as ChakraLink,
  SimpleGrid,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Text,
  VStack,
  useToast,
} from "@chakra-ui/react";
import {
  IoAdd,
  IoCloudUploadOutline,
  IoCreateOutline,
  IoTrashOutline,
} from "react-icons/io5";
import { Link, useNavigate } from "react-router-dom";
import { SubmitHandler, useForm } from "react-hook-form";
import { useAtom } from "jotai";
import {
  employeeInfoAtom,
  employeeInfoInitial,
  employeeContractAtom,
  passwordChangeStatusAtom,
  isGoogleSSOAtom,
  loginImageInfoAtom,
  termsAgreementAtom,
  accessTokenInfoAtom,
} from "../store";
import {
  SelectionLoginUser,
  LoginErrorCode,
  LoginFormData,
  LoginMethod,
} from "../types";
import { BasicPageLayout } from "../components/templates/BasicPageLayout";
import { HIDE_LANGUAGE_SELECTION, USE_TWOOCA_SSO } from "../environments";
import { ColorPicker } from "../components/molecules/ColorPicker";
import { DB, useAddUserToDb } from "../utils/db";
import { useLiveQuery } from "dexie-react-hooks";
import { SelectionLoginUserButton } from "../components/atoms/SelectionLoginUserButton";
import { LoadingOverlay } from "../components/atoms/LoadingOverlay";
import {
  IdInput,
  PasswordInput,
  TextInput,
} from "../components/atoms/AppInputs";
import { SimpleAlertDialog } from "../components/atoms/SimpleAlertDialog";
import { LanguageSelector } from "../components/atoms/LanguageSelector";
import { PrimarySwitch } from "../components/atoms/PrimarySwitch";
import { atomWithStorage } from "jotai/utils";

import { useSetSetting } from "../utils/setting";
import { homeSplashDisplayAtom } from "../components/atoms/ResultHome/HomeAtoms";
import { GoogleLoginButton } from "../components/molecules/GoogleLoginButton";
import { BannerImage } from "../components/atoms/BannerImage";
import { useGetAccessToken } from "../utils/token";
import { useModifiedTranslation } from "../hooks/useModifiedTranslation";
import { useInAppMicCheck } from "../hooks/useInAppMicCheck";
import { VersionCheckModal } from "../components/molecules/VersionCheckModal";
import { TwooCaLoginButton } from "../components/molecules/TwooCaLoginButton";
import { LoginErrorModal } from "../components/molecules/LoginErrorModal";
import { TermsAgreementModal } from "../components/molecules/TermsAgreementModal";
import { voislogApi } from "../utils/apiClients/voislogApi";
import { ApiError } from "../utils/apiClients/ApiError";
import { useColorToken } from "../hooks/useColorToken";
import { useDefaultPath } from "../hooks/useDefaultPath";
import Papa, { ParseError } from "papaparse";

// ============================================================
// Constants
// ============================================================
const COLOR_LIST = [
  { color: "#F9E1E1", label: "pink" },
  { color: "#E6B7EF", label: "purple" },
  { color: "#AEADE6", label: "indigo" },
  { color: "#8FE3E1", label: "sky-blue" },
  { color: "#A0E188", label: "green" },
  { color: "#E6E681", label: "yellow" },
];
const DEFAULT_COLOR_INDEX = 0;

const tabIndexAtom = atomWithStorage("loginPageTab", 0);

// ============================================================
// UI Components
// ============================================================

// EditModeToggle
type EditModeToggleProps = Readonly<{
  label: string;
  isEditing: boolean;
  onChange: (isEditing: boolean) => void;
}>;

function EditModeToggle({
  label,
  isEditing,
  onChange,
}: EditModeToggleProps): ReactElement {
  return (
    <FormControl
      display="flex"
      alignItems="center"
      justifyContent="flex-end"
      px={2}
      pt={1}
      pb={4}
    >
      <FormLabel htmlFor="editing-switch" mb={0} mr={1}>
        {label}
      </FormLabel>
      <PrimarySwitch
        id="editing-switch"
        data-testid="selection-login-edit-switch"
        isChecked={isEditing}
        onChange={(e) => onChange(e.target.checked)}
      />
    </FormControl>
  );
}

function NormalLoginForm(
  props: Readonly<{
    onInvalidUserInfo: (errorCode: LoginErrorCode) => void;
  }>,
): ReactElement {
  const [, setIsGoogleSSO] = useAtom(isGoogleSSOAtom);
  const {
    register,
    handleSubmit,
    formState: { isSubmitting },
  } = useForm<LoginFormData>();
  const [isCheckedToSaveUser, setIsCheckedToSaveUser] = useState(false);
  const [nickname, setNickname] = useState("");
  const [selectedColor, setSelectedColor] = useState(
    COLOR_LIST[DEFAULT_COLOR_INDEX].color,
  );
  const t = useModifiedTranslation();
  const [, setAccessTokenInfo] = useAtom(accessTokenInfoAtom);
  const getAccessToken = useGetAccessToken();
  // アプリ設定一括取得（/app_settings）と状態保持 前準備
  const setSettingInfo = useSetSetting();
  const addUserToDb = useAddUserToDb();

  const onSubmit: SubmitHandler<LoginFormData> = async (data) => {
    // GoogleSSO中ならキャンセルする
    window.google?.accounts?.id?.cancel();

    // PSTアカウントによる代理ログイン機能対応
    const token = await getAccessToken(
      LoginMethod.LoginForm,
      props.onInvalidUserInfo,
      data,
    );

    if (!token) return;

    // 選択ログインにユーザ追加
    if (isCheckedToSaveUser) {
      await addUserToDb({
        email: data.email,
        password: data.password,
        color: selectedColor,
        nickname,
        sortKey: "",
      });
    }

    setAccessTokenInfo(token);

    // アプリ設定取得（/app_settings）と状態保持 実施
    setSettingInfo(token);
  };

  return (
    <Box as="form" onSubmit={handleSubmit(onSubmit)} h="full">
      <Flex direction="column" align="center" h="full">
        <Flex direction="column" gap={2} p={{ base: 5, md: 10 }}>
          <IdInput
            data-testid="login-id"
            {...register("email", { required: true })}
            multiple // PSTアカウントによる代理ログイン機能対応
          />
          <PasswordInput
            data-testid="login-password"
            input={{ ...register("password", { required: true }) }}
          />

          {/* 選択ログイン保存スイッチ */}
          <FormControl
            display="flex"
            alignItems="center"
            justifyContent="flex-end"
          >
            <FormLabel htmlFor="email-alerts" mb={0} mr={1} fontSize={13}>
              {t("Login.addToSelectionLoginUsers")}
            </FormLabel>
            <PrimarySwitch
              id="email-alerts"
              data-testid="login-save-user-switch"
              onChange={(e) => setIsCheckedToSaveUser(e.target.checked)}
            />
          </FormControl>

          {/* Collapse内の要素のOutlineが途切れてしまうため、要素を少し大きしておいてpaddingを指定する */}
          {/* https://github.com/chakra-ui/chakra-ui/issues/6049 */}
          <Box width="calc(100% + 4px)" ml="-2px">
            <Collapse in={isCheckedToSaveUser} unmountOnExit>
              <Box px="2px">
                <TextInput
                  data-testid="login-nickname"
                  mt={1}
                  mb={5}
                  placeholder={t("Login.nickname")}
                  value={nickname}
                  onChange={(e) => setNickname(e.target.value)}
                  bg="common.base"
                />
                <Text mb={1} textAlign="center">
                  {t("Login.selectColor")}
                </Text>
                <ColorPicker
                  justifyContent="center"
                  spacing={{ base: 2, md: 5 }}
                  colorList={COLOR_LIST}
                  defaultIndex={DEFAULT_COLOR_INDEX}
                  onColorChange={(colorIndex) =>
                    setSelectedColor(COLOR_LIST[colorIndex].color)
                  }
                />
              </Box>
            </Collapse>
          </Box>

          <ChakraLink
            as={Link}
            data-testid="login-forgot-password"
            to="../password-reset"
            color="primary.theme_lv1"
            mx="auto"
          >
            {t("Login.forgotPassword")}
          </ChakraLink>
        </Flex>
        <VStack gap={1} w="full" pb={3}>
          <Button
            data-testid="login-submit"
            type="submit"
            variant="btn_primary"
            w="90%"
            size="lg"
            isLoading={isSubmitting}
            onClick={() => setIsGoogleSSO(false)}
          >
            {t("Login.login")}
          </Button>
          <Flex gap={2} justifyContent="space-evenly" flexWrap="wrap" w="90%">
            <GoogleLoginButton onInvalidUserInfo={props.onInvalidUserInfo} />
            {USE_TWOOCA_SSO && <TwooCaLoginButton />}
          </Flex>
        </VStack>
      </Flex>
    </Box>
  );
}

// UserListItem
type UserListItemProps = Readonly<{
  user: SelectionLoginUser;
  isEditing: boolean;
  isLoginProcessing: boolean;
  isDragging?: boolean;
  dragListeners?: Record<string, unknown>;
  onLogin: (user: SelectionLoginUser) => void;
  onEdit: (user: SelectionLoginUser) => void;
  onDelete: (user: SelectionLoginUser) => void;
}>;

function UserListItemContent({
  user,
  isEditing,
  isLoginProcessing,
  isDragging,
  dragListeners,
  onLogin,
  onEdit,
  onDelete,
}: UserListItemProps): ReactElement {
  const boxShadowColor = useColorToken("primary.bg_lv1");

  return (
    <Flex opacity={isDragging ? 0.5 : 1}>
      <Box
        display="inline-block"
        w={isEditing ? "calc(100% - 50px)" : "full"}
        transition="width 0.3s"
        cursor={isEditing ? "grab" : "default"}
        style={{ touchAction: isEditing ? "none" : "auto" }}
        {...(isEditing ? dragListeners : {})}
      >
        <SelectionLoginUserButton
          user={user}
          disabled={isLoginProcessing}
          onClick={() => onLogin(user)}
        />
      </Box>
      <Flex
        transition="max-width 0.3s"
        flexDirection="column"
        justifyContent="space-around"
        alignItems="center"
        maxW={isEditing ? "50px" : 0}
        w="50px"
        overflow="hidden"
      >
        {[
          {
            Icon: IoCreateOutline,
            color: "teal",
            label: "Edit User",
            testId: "selection-login-edit-user",
            onClick: onEdit,
          },
          {
            Icon: IoTrashOutline,
            color: "red",
            label: "Delete User",
            testId: "selection-login-delete-user",
            onClick: onDelete,
          },
        ].map((item, itemIndex) => (
          <IconButton
            key={itemIndex}
            aria-label={item.label}
            data-testid={item.testId}
            isRound
            rounded="full"
            variant="ghost"
            colorScheme={item.color}
            boxShadow={`0 3px 10px ${boxShadowColor}`}
            size="sm"
            minW="1.5rem"
            w="1.5rem"
            h="1.5rem"
            icon={<item.Icon size="1rem" />}
            onClick={() => item.onClick(user)}
          />
        ))}
      </Flex>
    </Flex>
  );
}

function UserListItem(props: UserListItemProps): ReactElement {
  const { user, isEditing } = props;
  const id = String(user.id);

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id, disabled: !isEditing });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <Box ref={setNodeRef} style={style} {...attributes}>
      <UserListItemContent
        {...props}
        isDragging={isDragging}
        dragListeners={listeners}
      />
    </Box>
  );
}

// EmptyUserListPlaceholder
function EmptyUserListPlaceholder(): ReactElement {
  const boxShadowColor = useColorToken("login.login_button_bg");
  const t = useModifiedTranslation();

  return (
    <Box gridColumn="1 / -1" gridRow="span 2" w="full">
      <HStack
        width="full"
        height="full"
        px={{ base: 3 }}
        py={3}
        boxShadow={`0 3px 10px ${boxShadowColor}`}
        rounded={20}
        spacing={{ base: 3 }}
        background="white"
        color="common_text.dark"
        alignItems="center"
      >
        <Avatar backgroundColor="gray.300" size="lg" />
        <VStack align="flex-start" flex={1} overflow="hidden" spacing={2}>
          <Text
            fontSize="xl"
            lineHeight={1}
            color="common_text.dark"
            textAlign="left"
            width="full"
          >
            {t("Login.selectionLogin.emptyUserList")}
          </Text>
          <Text
            fontSize="lg"
            lineHeight={1}
            color="primary.text_lv2"
            textAlign="left"
            width="full"
          >
            {t("Login.selectionLogin.emptyUserList.subtext")}
          </Text>
        </VStack>
      </HStack>
    </Box>
  );
}

// AddUserButton
type AddUserButtonProps = Readonly<{
  label: string;
  disabled: boolean;
  onClick: () => void;
}>;

function AddUserButton({
  label,
  disabled,
  onClick,
}: AddUserButtonProps): ReactElement {
  return (
    <Box px={{ base: 7, md: 12 }} width="full" maxW="2xl">
      <Button
        variant="btn_primary"
        data-testid="selection-login-add-user"
        display="flex"
        alignItems="center"
        width="full"
        rightIcon={<IoAdd size={20} />}
        isDisabled={disabled}
        onClick={onClick}
      >
        <Text flex={1}>{label}</Text>
      </Button>
    </Box>
  );
}

// ImportCsvButton
type ImportCsvButtonProps = Readonly<{
  label: string;
  disabled: boolean;
  onImport: (file: File) => void;
}>;

function ImportCsvButton({
  label,
  disabled,
  onImport,
}: ImportCsvButtonProps): ReactElement {
  const handleClick = (): void => {
    const input = document.createElement("input");
    input.type = "file";
    input.accept = ".csv,text/csv";
    input.onchange = (event) => {
      const file = (event.target as HTMLInputElement).files?.[0];
      if (file) onImport(file);
    };
    input.click();
  };

  return (
    <Flex width="full" justifyContent="flex-end">
      <Button
        variant="ghost"
        data-testid="selection-login-import-csv"
        display="flex"
        alignItems="center"
        rightIcon={<IoCloudUploadOutline size={18} />}
        isDisabled={disabled}
        onClick={handleClick}
      >
        <Text flex={1}>{label}</Text>
      </Button>
    </Flex>
  );
}

// UserEditDrawer
type UserEditDrawerProps = Readonly<{
  isOpen: boolean;
  isAdditionMode: boolean;
  userToEdit: SelectionLoginUser | null;
  onClose: () => void;
  onCompleted: () => void;
  onInvalidUserInfo: (errorCode: LoginErrorCode) => void;
}>;

function UserEditDrawer({
  isOpen,
  isAdditionMode,
  userToEdit,
  onClose,
  onCompleted,
  onInvalidUserInfo,
}: UserEditDrawerProps): ReactElement {
  const t = useModifiedTranslation();

  return (
    <Drawer
      isOpen={isOpen}
      placement="bottom"
      blockScrollOnMount={false}
      onClose={onClose}
    >
      <DrawerOverlay />
      <DrawerContent
        alignItems="center"
        pb={5}
        backgroundColor="primary.bg_lv1"
      >
        <DrawerCloseButton />
        <DrawerHeader>
          {isAdditionMode
            ? t("Login.selectionLogin.addUser.title")
            : t("Login.selectionLogin.editUser.title")}
        </DrawerHeader>
        <DrawerBody maxW="2xl" w="full">
          <SelectionLoginUserForm
            onCompleted={onCompleted}
            onInvalidUserInfo={onInvalidUserInfo}
            userToEdit={userToEdit || undefined}
          />
        </DrawerBody>
      </DrawerContent>
    </Drawer>
  );
}

// ============================================================
// Page Components
// ============================================================

// SelectionLogin
function SelectionLogin(
  props: Readonly<{
    onInvalidUserInfo: (errorCode: LoginErrorCode) => void;
  }>,
): ReactElement {
  const rawUserList = useLiveQuery(() => DB.loadAllUsers());
  const [localUserList, setLocalUserList] = useState<SelectionLoginUser[]>([]);

  // DBからの読み込み時にローカルstateを更新
  useEffect(() => {
    if (!rawUserList) return;
    const sorted = [...rawUserList].sort((a, b) => {
      const aNum = Number(a.sortKey);
      const bNum = Number(b.sortKey);
      if (!isNaN(aNum) && !isNaN(bNum)) return aNum - bNum;
      return a.sortKey.localeCompare(b.sortKey);
    });
    setLocalUserList(sorted);
  }, [rawUserList]);

  const userList = localUserList;
  const [isLoginProcessing, setIsLoginProcessing] = useState(false);
  const [isAdditionFormOpen, setIsAdditionFormOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [userToDelete, setUserToDelete] = useState<SelectionLoginUser | null>(
    null,
  );
  const [userToEdit, setUserToEdit] = useState<SelectionLoginUser | null>(null);
  const [pendingCsvImport, setPendingCsvImport] = useState<
    Array<{ email: string; password: string; nickname: string }>
  >([]);
  const [, setAccessTokenInfo] = useAtom(accessTokenInfoAtom);
  const getAccessToken = useGetAccessToken();
  const t = useModifiedTranslation();
  const toast = useToast();
  // アプリ設定一括取得（/app_settings）と状態保持 前準備
  const setSettingInfo = useSetSetting();
  const csvParseErrorKeyByCode: Record<ParseError["code"], string> = {
    MissingQuotes: "Login.selectionLogin.csvImport.parseError.missingQuotes",
    UndetectableDelimiter:
      "Login.selectionLogin.csvImport.parseError.undetectableDelimiter",
    TooFewFields: "Login.selectionLogin.csvImport.parseError.tooFewFields",
    TooManyFields: "Login.selectionLogin.csvImport.parseError.tooManyFields",
    InvalidQuotes: "Login.selectionLogin.csvImport.parseError.invalidQuotes",
  };

  const showError = (message: string): void => {
    toast({
      title: t("Login.selectionLogin.csvImport.error"),
      description: message,
      status: "error",
      duration: 5000,
      isClosable: true,
    });
  };

  type ParsedCsvUser = Readonly<{
    email: string;
    password: string;
    nickname: string;
  }>;

  const saveParsedUsers = async (
    parsedUsers: ParsedCsvUser[],
  ): Promise<void> => {
    const existingUsers = await DB.loadAllUsers();
    const existingCount = existingUsers.length;

    for (let i = 0; i < parsedUsers.length; i++) {
      const { email, password, nickname } = parsedUsers[i];
      const colorIndex = (existingCount + i) % COLOR_LIST.length;
      const color = COLOR_LIST[colorIndex].color;
      const existingUser = await DB.loadUserByEmail(email);

      if (existingUser && existingUser.id !== undefined) {
        await DB.updateUser(existingUser.id, {
          ...existingUser,
          password,
          nickname,
        });
      } else {
        await DB.addUser({
          email,
          password,
          nickname,
          color,
          sortKey: "",
        });
      }
    }
  };

  const handleCsvImport = (file: File): void => {
    const parsedUsers: ParsedCsvUser[] = [];
    const seenEmails = new Set<string>();
    Papa.parse<Record<string, string>>(file, {
      header: true,
      skipEmptyLines: true,
      transformHeader: (h) => h.trim(),
      beforeFirstChunk: (chunk) => {
        const lines = chunk.split(/\r?\n/);
        return lines.length > 1 ? lines.slice(1).join("\n") : chunk;
      },
      error: (error) => showError(error.message),
      step: ({ data: row, errors }, parser) => {
        if (errors.length > 0) {
          const i18nKey = csvParseErrorKeyByCode[errors[0].code];
          showError(t(i18nKey));
          parser.abort();
          return;
        }

        const email = row["email"]?.trim() ?? "";
        const password = row["password"]?.trim() ?? "";
        const lastName = row["last_name"]?.trim() ?? "";
        const firstName = row["first_name"]?.trim() ?? "";
        const nickname = `${lastName} ${firstName}`.trim();

        if (email === "" || password === "") {
          showError(t("Login.selectionLogin.csvImport.emptyField"));
          parser.abort();
          return;
        }

        if (nickname === "") {
          showError(t("Login.selectionLogin.csvImport.emptyNickname"));
          parser.abort();
          return;
        }

        if (seenEmails.has(email)) {
          showError(t("Login.selectionLogin.csvImport.duplicateEmail"));
          parser.abort();
          return;
        }
        seenEmails.add(email);

        parsedUsers.push({
          email,
          password,
          nickname,
        });
      },
      complete: (results) => {
        if (results.meta.aborted) return;
        if (parsedUsers.length === 0) {
          showError(t("Login.selectionLogin.csvImport.emptyFile"));
          return;
        }

        // 件数確認ダイアログを表示
        setPendingCsvImport(parsedUsers);
      },
    });
  };

  const pendingAddedUsers = useMemo(() => {
    return pendingCsvImport.filter((csvUser) => {
      const existingUser = rawUserList?.find((u) => u.email === csvUser.email);
      return !existingUser;
    });
  }, [pendingCsvImport, rawUserList]);

  const pendingUpdatedUsers = useMemo(() => {
    return pendingCsvImport.filter((csvUser) => {
      const existingUser = rawUserList?.find((u) => u.email === csvUser.email);
      return !!existingUser;
    });
  }, [pendingCsvImport, rawUserList]);

  const login = async (selectionUser: SelectionLoginUser): Promise<void> => {
    // GoogleSSO中ならキャンセルする
    window.google?.accounts?.id?.cancel();

    const user: LoginFormData = {
      email: selectionUser.email,
      password: selectionUser.password,
    };
    setIsLoginProcessing(true);
    const token = await getAccessToken(
      LoginMethod.LoginForm,
      props.onInvalidUserInfo,
      user,
    );
    setIsLoginProcessing(false);
    if (!token) return;

    setAccessTokenInfo(token);

    // アプリ設定一括取得（/app_settings）と状態保持 実施
    setSettingInfo(token);
  };

  const onClickEdit = useCallback(
    (user: SelectionLoginUser) => setUserToEdit(user),
    [],
  );
  const onClickDelete = useCallback(
    (user: SelectionLoginUser) => setUserToDelete(user),
    [],
  );

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 5 },
    }),
  );

  const userIds = useMemo(
    () => userList?.map((u) => String(u.id)) ?? [],
    [userList],
  );

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;
      if (!over || active.id === over.id || !userList.length) return;

      const oldIndex = userList.findIndex((u) => String(u.id) === active.id);
      const newIndex = userList.findIndex((u) => String(u.id) === over.id);
      if (oldIndex === -1 || newIndex === -1) return;

      const newList = [...userList];
      const [moved] = newList.splice(oldIndex, 1);
      newList.splice(newIndex, 0, moved);

      // 即座にローカルstateを更新
      setLocalUserList(newList);

      // DBは非同期で更新
      (async () => {
        for (let i = 0; i < newList.length; i++) {
          const user = newList[i];
          if (user.id !== undefined) {
            await DB.updateUser(user.id, { ...user, sortKey: String(i) });
          }
        }
      })();
    },
    [userList],
  );

  const mobileMinChildWidth = "calc(var(--chakra-sizes-8) + 11ch)";
  const normalMinChildWidth = "calc(var(--chakra-sizes-8) + 20ch)";

  return (
    <>
      <EditModeToggle
        label={t("Login.selectionLogin.edit")}
        isEditing={isEditing}
        onChange={setIsEditing}
      />

      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd}
      >
        <SortableContext items={userIds} strategy={rectSortingStrategy}>
          <SimpleGrid
            fontSize="xl"
            minChildWidth={{
              base: mobileMinChildWidth,
              sm: normalMinChildWidth,
            }}
            spacing={5}
            px={2}
            flex={1}
            alignContent="flex-start"
            minH={0}
            overflowY="auto"
          >
            {userList?.length === 0 && <EmptyUserListPlaceholder />}
            {userList?.map((user) => (
              <UserListItem
                key={user.id}
                user={user}
                isEditing={isEditing}
                isLoginProcessing={isLoginProcessing}
                onLogin={login}
                onEdit={onClickEdit}
                onDelete={onClickDelete}
              />
            ))}
          </SimpleGrid>
        </SortableContext>
      </DndContext>

      {isEditing && (
        <>
          <Text fontSize="xs" color="gray.500" textAlign="center" mt={2} mb={1}>
            {t("Login.selectionLogin.editModeHint")}
          </Text>
          <AddUserButton
            label={t("Login.selectionLogin.addUser")}
            disabled={isLoginProcessing}
            onClick={() => setIsAdditionFormOpen(true)}
          />
          <ImportCsvButton
            label={t("Login.selectionLogin.csvImport")}
            disabled={isLoginProcessing}
            onImport={handleCsvImport}
          />
        </>
      )}

      <LoadingOverlay isOpen={isLoginProcessing} />

      {/* ユーザー追加/編集 */}
      <UserEditDrawer
        isOpen={isAdditionFormOpen || !!userToEdit}
        isAdditionMode={isAdditionFormOpen}
        userToEdit={userToEdit}
        onClose={() => {
          setIsAdditionFormOpen(false);
          setUserToEdit(null);
        }}
        onCompleted={() => {
          setIsAdditionFormOpen(false);
          setUserToEdit(null);
        }}
        onInvalidUserInfo={props.onInvalidUserInfo}
      />

      {/* ユーザー削除確認 */}
      <SimpleAlertDialog
        isOpen={!!userToDelete}
        okTestId="selection-login-delete-confirm"
        ok={t("Login.selectionLogin.deleteUser.yes")}
        cancel={t("Login.selectionLogin.deleteUser.cancel")}
        title={t("Login.selectionLogin.deleteUser.title")}
        description={t("Login.selectionLogin.deleteUser.description")}
        onOk={() => {
          if (userToDelete?.id) {
            DB.deleteUser(userToDelete.id).then();
          }
          setUserToDelete(null);
        }}
        onClose={() => setUserToDelete(null)}
      />

      {/* CSVインポート確認 */}
      <SimpleAlertDialog
        isOpen={0 < pendingCsvImport.length}
        okTestId="selection-login-import-confirm"
        ok={t("Login.selectionLogin.csvImport.ok")}
        cancel={t("Login.selectionLogin.csvImport.cancel")}
        title={t("Login.selectionLogin.csvImport.confirmTitle")}
        description={(() => {
          const updated = t(
            "Login.selectionLogin.csvImport.confirmDescriptionUpdated",
          );
          const added = t(
            "Login.selectionLogin.csvImport.confirmDescriptionAdded",
          );
          const count = t(
            "Login.selectionLogin.csvImport.confirmDescriptionCount",
          );
          return `${updated}: ${pendingUpdatedUsers.length} ${count}, ${added}: ${pendingAddedUsers.length} ${count}`;
        })()}
        onOk={async () => {
          try {
            await saveParsedUsers(pendingCsvImport);
            toast({
              title: t("Login.selectionLogin.csvImport.complete"),
              description: t(
                "Login.selectionLogin.csvImport.completeDescription",
              ),
              status: "success",
              duration: 5000,
              isClosable: true,
            });
          } catch (error) {
            showError(error instanceof Error ? error.message : String(error));
          }
          setPendingCsvImport([]);
        }}
        onClose={() => setPendingCsvImport([])}
      />
    </>
  );
}

// SelectionLoginUserForm
function SelectionLoginUserForm(
  props: Readonly<{
    onCompleted: () => void;
    onInvalidUserInfo: (errorCode: LoginErrorCode) => void;
    userToEdit?: SelectionLoginUser;
  }>,
): ReactElement {
  const t = useModifiedTranslation();
  const {
    register,
    handleSubmit,
    setValue,
    formState: { isSubmitting },
  } = useForm<SelectionLoginUser>();
  const addUserToDb = useAddUserToDb();
  const getAccessToken = useGetAccessToken();

  const onSubmit: SubmitHandler<SelectionLoginUser> = async (data) => {
    const user: LoginFormData = {
      email: data.email,
      password: data.password,
    };
    const token = await getAccessToken(
      LoginMethod.LoginForm,
      props.onInvalidUserInfo,
      user,
    );
    if (!token) return;

    if (props.userToEdit?.id !== undefined) {
      await DB.updateUser(props.userToEdit.id, {
        ...data,
        sortKey: props.userToEdit.sortKey,
      });
    } else {
      const success = await addUserToDb({ ...data, sortKey: "" });
      if (!success) return;
    }
    props.onCompleted();
  };

  const defaultColorIndex = useMemo(() => {
    if (props.userToEdit) {
      const tmpIndex = COLOR_LIST.findIndex(
        (colorItem) => colorItem.color === props.userToEdit?.color,
      );
      return tmpIndex >= 0 ? tmpIndex : DEFAULT_COLOR_INDEX;
    } else {
      return DEFAULT_COLOR_INDEX;
    }
  }, [props.userToEdit]);

  return (
    <Flex
      as="form"
      flexDirection="column"
      rowGap={4}
      onSubmit={handleSubmit(onSubmit)}
    >
      <IdInput
        data-testid="user-form-id"
        defaultValue={props.userToEdit ? props.userToEdit.email : undefined}
        {...register("email")}
      />
      <PasswordInput
        data-testid="user-form-password"
        input={{
          defaultValue: props.userToEdit
            ? props.userToEdit.password
            : undefined,
          ...register("password"),
        }}
      />
      <TextInput
        data-testid="user-form-nickname"
        defaultValue={props.userToEdit ? props.userToEdit.nickname : undefined}
        placeholder={t("Login.nickname")}
        {...register("nickname")}
        bg="common.base"
      />
      <Text textAlign="center">{t("Login.selectColor")}</Text>
      <input
        type="hidden"
        value={COLOR_LIST[defaultColorIndex].color}
        {...register("color")}
      />
      <ColorPicker
        margin="0 auto"
        spacing={{ base: 2, md: 5 }}
        colorList={COLOR_LIST}
        defaultIndex={defaultColorIndex}
        onColorChange={(colorIndex) =>
          setValue("color", COLOR_LIST[colorIndex].color)
        }
      />
      <Button
        variant="btn_primary"
        data-testid="user-form-submit"
        w="full"
        type="submit"
        disabled={isSubmitting}
      >
        {t("Login.selectionLogin.addUser.submit")}
      </Button>
    </Flex>
  );
}

// Login (Main Page)
export function Login(): ReactElement {
  const [isLoginError, setIsLoginError] = useState(false);
  const [tabIndex, setTabIndex] = useAtom(tabIndexAtom);
  const [showTermsModal, setShowTermsModal] = useState(false);
  const [termsAgreed, setTermsAgreed] = useAtom(termsAgreementAtom);
  const [privacyUrl, setPrivacyUrl] = useState<string | null>(null);
  const [privacyVersion, setPrivacyVersion] = useState<number | null>(null);
  const [termsUrl, setTermsUrl] = useState<string | null>(null);
  const [termsVersion, setTermsVersion] = useState<number | null>(null);

  const handleTabsChange = (index: number): void => {
    setTabIndex(index);
  };

  const navigate = useNavigate();
  const defaultPath = useDefaultPath();

  // ログイン画面に表示する画像のURLをlocalStorageに保持管理用
  const [accessTokenInfo] = useAtom(accessTokenInfoAtom);
  const [loginImageInfo] = useAtom(loginImageInfoAtom);
  const [, setHomeSplashDisplay] = useAtom(homeSplashDisplayAtom);
  const [employeeInfo, setEmployeeInfo] = useAtom(employeeInfoAtom);
  const [, setEmployeeContract] = useAtom(employeeContractAtom);
  const [passwordChangeStatus, setPasswordChangeStatus] = useAtom(
    passwordChangeStatusAtom,
  );

  const t = useModifiedTranslation();
  useInAppMicCheck();

  const [errorCode, setErrorCode] = useState<LoginErrorCode | null>(null);
  const handleInvalidUserInfo = (code: LoginErrorCode): void => {
    setIsLoginError(true);
    setErrorCode(code);
  };

  useEffect(() => {
    setHomeSplashDisplay(true);
    setPasswordChangeStatus(false);
    setEmployeeInfo(employeeInfoInitial);
  }, [setHomeSplashDisplay, setPasswordChangeStatus, setEmployeeInfo]);

  const fetchAgreements = useCallback(async () => {
    const { data, error, response } = await voislogApi.GET(
      "/v2/core/policies/users/{user_id_or_me}/agreements",
      { params: { path: { user_id_or_me: "me" } } },
    );
    if (error) throw new ApiError(response, "get", error);
    const { privacy, terms } = data;

    setEmployeeContract({
      privacy_policy: privacy.url ?? "",
      terms_of_service: terms.url ?? "",
    });

    if (privacy.user_agreement != privacy.version) {
      setPrivacyUrl(privacy.url);
      setPrivacyVersion(privacy.version);
    }

    if (terms.user_agreement != terms.version) {
      setTermsUrl(terms.url);
      setTermsVersion(terms.version);
    }

    if (
      privacy.user_agreement === privacy.version &&
      terms.user_agreement === terms.version
    ) {
      // 最新規約に同意済み
      setTermsAgreed(true);
    }
  }, [
    setEmployeeContract,
    setTermsAgreed,
    setPrivacyUrl,
    setPrivacyVersion,
    setTermsUrl,
    setTermsVersion,
  ]);

  useEffect(() => {
    // NOTE: ログイン実施ごとにチェックするため、アクセストークンの更新を監視
    if (!accessTokenInfo?.access_token) return;

    if (privacyUrl || termsUrl) {
      // 利用規約に同意していないなら利用規約モーダルを表示
      setShowTermsModal(true);
    }
  }, [privacyUrl, termsUrl, accessTokenInfo?.access_token]);

  useEffect(() => {
    if (employeeInfo.employee_id && !termsAgreed) {
      fetchAgreements();
      return;
    }

    if (passwordChangeStatus || employeeInfo.employee_id) {
      navigate(defaultPath);
    }
  }, [
    fetchAgreements,
    employeeInfo.employee_id,
    navigate,
    defaultPath,
    passwordChangeStatus,
    termsAgreed,
    setTermsAgreed,
  ]);

  const handleTermsAgree = useCallback(() => {
    const body: { privacy?: number; terms?: number } = {};
    if (privacyVersion) {
      body["privacy"] = privacyVersion;
    }
    if (termsVersion) {
      body["terms"] = termsVersion;
    }

    voislogApi
      .POST("/v2/core/policies/users/{user_id_or_me}/agreements", {
        params: { path: { user_id_or_me: "me" } },
        body,
      })
      .then(({ error, response }) => {
        if (error) throw new ApiError(response, "post", error);
        setTermsAgreed(true);
        setShowTermsModal(false);
      });
  }, [setTermsAgreed, privacyVersion, termsVersion]);

  const handleTermsModalClose = (): void => {
    setShowTermsModal(false);
  };

  return (
    <Flex flexDirection="column" height="full">
      <VersionCheckModal />
      <TermsAgreementModal
        isOpen={showTermsModal}
        onClose={handleTermsModalClose}
        onAgree={handleTermsAgree}
        privacyUrl={privacyUrl}
        termsUrl={termsUrl}
      />
      <LoginErrorModal
        isOpen={isLoginError}
        errorCode={errorCode}
        onClose={() => setIsLoginError(false)}
      />

      {/* ログイン後にAPIで取得したアプリのロゴを表示 */}
      <BannerImage imageInfo={loginImageInfo} />
      <Flex flexDirection="column" alignItems="center" flex={1}>
        <BasicPageLayout>
          <Flex
            position="relative"
            justifyContent="center"
            alignItems="center"
            width="100%"
          >
            <Text fontWeight="500">ver.{__APP_VERSION__}</Text>
            {!HIDE_LANGUAGE_SELECTION && (
              <Box position="absolute" right={0}>
                <LanguageSelector to="login" />
              </Box>
            )}
          </Flex>
        </BasicPageLayout>
        <Tabs
          isFitted
          index={tabIndex}
          onChange={handleTabsChange}
          variant="unstyled"
          display="flex"
          flexDirection="column"
          width="100%"
          maxW="2xl"
          maxH="full"
          h="full"
          overflow="hidden"
        >
          <TabList bgColor="login.login_button_bg" borderRadius="full">
            {[t("Login.normalLogin"), t("Login.selectionLogin")].map(
              (tabItem, tabIndex) => (
                <Tab
                  key={tabIndex}
                  data-testid={
                    tabIndex === 0 ? "tab-normal-login" : "tab-selection-login"
                  }
                  fontWeight="bold"
                  fontSize={14}
                  color="common_text.gray"
                  margin={1.5}
                  borderRadius="full"
                  _active={{ backgroundColor: "white" }}
                  sx={{
                    "@media (hover: hover)": {
                      "&:hover": { backgroundColor: "white" },
                    },
                  }}
                  _selected={{
                    color: "common_text.dark",
                    bgColor: "white",
                    margin: 1.5,
                    borderRadius: "full",
                  }}
                >
                  {tabItem}
                </Tab>
              ),
            )}
          </TabList>
          <TabPanels maxH="full" h="full" overflow="auto">
            <TabPanel p={1} h="full">
              <NormalLoginForm onInvalidUserInfo={handleInvalidUserInfo} />
            </TabPanel>
            <TabPanel p={1} h="full" minH={0} overflow="hidden">
              <Flex
                direction="column"
                maxW="2xl"
                maxH="full"
                w="100%"
                h="full"
                overflow="hidden"
                flex={1}
                minH={0}
              >
                <SelectionLogin onInvalidUserInfo={handleInvalidUserInfo} />
              </Flex>
            </TabPanel>
          </TabPanels>
        </Tabs>
      </Flex>
    </Flex>
  );
}
